/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace invgamma
*/
var invgamma = {};

/**
* @name cdf
* @memberof invgamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/invgamma/cdf}
*/
import cdf from './../../../../base/dists/invgamma/cdf';
setReadOnly( invgamma, 'cdf', cdf );

/**
* @name InvGamma
* @memberof invgamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/invgamma/ctor}
*/
import InvGamma from './../../../../base/dists/invgamma/ctor';
setReadOnly( invgamma, 'InvGamma', InvGamma );

/**
* @name entropy
* @memberof invgamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/invgamma/entropy}
*/
import entropy from './../../../../base/dists/invgamma/entropy';
setReadOnly( invgamma, 'entropy', entropy );

/**
* @name kurtosis
* @memberof invgamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/invgamma/kurtosis}
*/
import kurtosis from './../../../../base/dists/invgamma/kurtosis';
setReadOnly( invgamma, 'kurtosis', kurtosis );

/**
* @name logpdf
* @memberof invgamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/invgamma/logpdf}
*/
import logpdf from './../../../../base/dists/invgamma/logpdf';
setReadOnly( invgamma, 'logpdf', logpdf );

/**
* @name mean
* @memberof invgamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/invgamma/mean}
*/
import mean from './../../../../base/dists/invgamma/mean';
setReadOnly( invgamma, 'mean', mean );

/**
* @name mode
* @memberof invgamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/invgamma/mode}
*/
import mode from './../../../../base/dists/invgamma/mode';
setReadOnly( invgamma, 'mode', mode );

/**
* @name pdf
* @memberof invgamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/invgamma/pdf}
*/
import pdf from './../../../../base/dists/invgamma/pdf';
setReadOnly( invgamma, 'pdf', pdf );

/**
* @name quantile
* @memberof invgamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/invgamma/quantile}
*/
import quantile from './../../../../base/dists/invgamma/quantile';
setReadOnly( invgamma, 'quantile', quantile );

/**
* @name skewness
* @memberof invgamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/invgamma/skewness}
*/
import skewness from './../../../../base/dists/invgamma/skewness';
setReadOnly( invgamma, 'skewness', skewness );

/**
* @name stdev
* @memberof invgamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/invgamma/stdev}
*/
import stdev from './../../../../base/dists/invgamma/stdev';
setReadOnly( invgamma, 'stdev', stdev );

/**
* @name variance
* @memberof invgamma
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/invgamma/variance}
*/
import variance from './../../../../base/dists/invgamma/variance';
setReadOnly( invgamma, 'variance', variance );


// EXPORTS //

export default invgamma;
